import axios from 'axios';

// Get the API URL from environment variables
// Get the API URL from environment variables
// Using relative path '/api' to leverage package.json proxy for local dev (avoids CORS)
const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Configura axios per includere automaticamente il token di autenticazione
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor per gestire errori di autenticazione
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Token scaduto o non valido - rimuovi dal localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            // Redirect al login
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

class ArtifactService {
    /**
     * Get all artifacts for a specific course (Original logic)
     * @param {string} courseId 
     * @returns {Promise<Array>} List of artifacts
     */
    async getArtifacts(courseId) {
        try {
            const response = await axios.get(`${API_URL}/artefact/course/${courseId}/list`);
            console.log('Get Artifacts Response:', response.data);

            // Handle different response structures
            if (Array.isArray(response.data)) {
                return response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                return response.data.data; // Common pagination/wrapper pattern
            } else if (response.data && Array.isArray(response.data.artefacts)) {
                return response.data.artefacts; // Possible key name
            }

            // If we can't find an array, return empty to prevent crash
            console.warn('Unexpected artifacts response structure, defaulting to empty array.');
            return [];
        } catch (error) {
            console.error('Error fetching artifacts:', error);
            throw error;
        }
    }

    /**
     * Get full artifacts response including metadata
     * @param {string} courseId 
     * @returns {Promise<Object>} Full API response
     */
    async getArtifactsResponse(courseId) {
        try {
            const response = await axios.get(`${API_URL}/artefact/course/${courseId}/list`);
            return response.data;
        } catch (error) {
            console.error('Error fetching artifacts response:', error);
            throw error;
        }
    }

    /**
     * Create a new artifact
     * @param {string} courseId 
     * @param {string} type - 'mappa', 'approfondimento', 'sintesi', 'quiz', 'flashcard'
     * @param {string} userPrompt 
     * @param {Object} options - Optional config like quiz_size, quiz_difficulty, synthesis_level, chat_history
     * @returns {Promise<Object>} Created artifact
     */
    async createArtifact(courseId, type, userPrompt, options = {}) {
        try {
            const payload = {
                course_id: parseInt(courseId, 10), // Ensure integer
                type: type,
                user_prompt: userPrompt,
                ...options // Spread optional parameters (quiz_size, etc.)
            };

            console.log('Sending Payload:', payload);

            // Set timeout to 10 minutes (600000ms) for large AI generations
            const response = await axios.post(`${API_URL}/artefact/create`, payload, {
                timeout: 600000
            });
            return response.data;
        } catch (error) {
            console.error('Error creating artifact:', error);
            if (error.response) {
                console.error('Response Status:', error.response.status);
                // Stringify to prevent console collapsing
                console.error('Response Data:', JSON.stringify(error.response.data, null, 2));
            }
            throw error;
        }
    }

    /**
     * Delete an artifact
     * @param {string|number} id The artifact ID
     * @returns {Promise<any>}
     */
    async deleteArtifact(id) {
        try {
            console.log(`Attempting to DELETE artifact: ${id} at ${API_URL}/artefact/${id}`);
            const response = await axios.delete(`${API_URL}/artefact/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting artifact:', error);
            if (error.response) {
                console.error('DELETE Error Status:', error.response.status);
                console.error('DELETE Error Data:', error.response.data);
                throw new Error(`Errore Server: ${error.response.status}`);
            }
            throw new Error('Errore di connessione');
        }
    }

    /**
     * Update an artifact (e.g. rename)
     * @param {string|number} id The artifact ID
     * @param {Object} data The data to update (e.g. { name: "New Name" })
     * @returns {Promise<any>}
     */
    async updateArtifact(id, data) {
        try {
            console.log(`Attempting to UPDATE artifact: ${id} with`, data);
            const response = await axios.put(`${API_URL}/artefact/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating artifact:', error);
            if (error.response) {
                console.error('UPDATE Error Status:', error.response.status);
                console.error('UPDATE Error Data:', error.response.data);
                throw new Error(`Errore Server: ${error.response.status}`);
            }
            throw new Error('Errore di connessione');
        }
    }
}

export default new ArtifactService();
